import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  AccountMergeRequestResource,
  ListAccountMergeRequestsRequest,
} from '@/features/admin/api/resources/account-merge-request'
import type {
  CreateWorkspacePostingRequest,
  GetWorkspacePostingAggregationRequest,
  ListWorkspacePostingsRequest,
  UpdateWorkspacePostingRequest,
  WorkspacePostingAggregationResource,
  WorkspacePostingResource,
} from '@/features/admin/api/resources/workspace-posting'
import { toURLSearchParams } from '@/utils/url-util'

/**
 * Admin endpoints of the IAM API.
 */
export class AdminIamApi {
  readonly accountMergeRequests
  readonly workspacePostings

  constructor(options: ApiClientOptions) {
    this.accountMergeRequests = new AccountMergeRequestApi(options)
    this.workspacePostings = new WorkspacePostingApi(options)
  }
}

/**
 * API for manual account merge requests.
 */
export class AccountMergeRequestApi extends ApiClient {
  list(
    request: ListAccountMergeRequestsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<AccountMergeRequestResource>> {
    return this._get(
      'v1/manual-merge-requests',
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  link(id: string): Promise<AccountMergeRequestResource> {
    return this._post(`v1/manual-merge-requests/${id}/link`, undefined)
  }

  reject(id: string): Promise<AccountMergeRequestResource> {
    return this._post(`v1/manual-merge-requests/${id}/reject`, undefined)
  }

  ignore(id: string): Promise<AccountMergeRequestResource> {
    return this._post(`v1/manual-merge-requests/${id}/ignore`, undefined)
  }
}

/**
 * API for workspace postings.
 */
export class WorkspacePostingApi extends ApiClient {
  create(
    workspaceId: string,
    request: CreateWorkspacePostingRequest,
  ): Promise<WorkspacePostingResource> {
    return this._post(`v3/workspaces/${workspaceId}/postings`, request)
  }

  publish(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/publish`, undefined)
  }

  markInReview(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/review`, undefined)
  }

  get(id: string, signal?: AbortSignal): Promise<WorkspacePostingResource> {
    return this._get(`v3/postings/${id}`, undefined, { signal })
  }

  updateDraft(
    id: string,
    request: UpdateWorkspacePostingRequest,
  ): Promise<WorkspacePostingResource> {
    return this._patch(`v3/postings/${id}`, request)
  }

  list(
    request: ListWorkspacePostingsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<WorkspacePostingResource>> {
    const params = toURLSearchParams(
      { ...request },
      { arrayStyle: 'multi-key-bracket' },
    )
    return this._get(`v3/postings`, params, { signal })
  }

  getAggregation(
    request: GetWorkspacePostingAggregationRequest,
    signal?: AbortSignal,
  ): Promise<WorkspacePostingAggregationResource[]> {
    const params = toURLSearchParams(
      { ...request },
      { arrayStyle: 'multi-key-bracket' },
    )
    return this._get(`v3/postings/aggregation`, params, { signal })
  }

  inviteApplicant(id: string, applicantPublicId: string): Promise<void> {
    return this._patch(`v3/postings/${id}/invite`, {
      applicant_public_id: applicantPublicId,
    })
  }

  rescindInvitation(id: string, applicantPublicId: string): Promise<void> {
    return this._patch(`v3/postings/${id}/rescind-invitation`, {
      applicant_public_id: applicantPublicId,
    })
  }

  reactivate(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/reactivate`, undefined)
  }

  manuallyClose(id: string): Promise<void> {
    return this._patch(`v3/postings/${id}/close`, undefined)
  }
}
